import React from 'react';

import './App.css';
import { AppContent } from './AppContent';
import { GameStateManager } from './contexts/GameStateManager';
import { TestGlobalManager } from './contexts/TestGlobalManager';
import { ToastManagerProvider } from './contexts/ToastManagerContext';
import { useBuildings } from './hooks/useBuildings';
import { useDebugState } from './hooks/useDebugState';
import { useWorld } from './hooks/useWorld';
// Import test command to expose test functions to window object
import './TestCommand';

// Inner component that uses toast context
const AppWithProviders: React.FC = () => {
  // Building state management
  const buildingState = useBuildings();
  const { debugState } = useDebugState();

  // World and game state management
  const world = useWorld(buildingState, debugState);

  const worldState = {
    isLoading: world.isLoading,
    playerPos: world.playerPos,
    playerInventory: world.playerInventory,
    gameMap: world.gameMap,
    buildingLayer: world.buildingLayer,
    beltContentLayer: world.beltContentLayer,
    assemblerInventoryLayer: world.assemblerInventoryLayer,
    chestInventoryLayer: world.chestInventoryLayer,
    rejectedItemsLayer: world.rejectedItemsLayer,
    minerTimerLayer: world.minerTimerLayer,
    prestigePoints: world.prestigePoints,
    prestigeCount: world.prestigeCount,
    cameraPos: world.cameraPos,
    currentBiome: world.currentBiome,
    playerFacing: world.playerFacing,
    zoomLevel: world.zoomLevel,
    tickSpeedMultiplier: world.tickSpeedMultiplier,
    powerProduction: world.powerProduction,
    powerDemand: world.powerDemand,
    powerSatisfaction: world.powerSatisfaction,
    powerHistory: world.powerHistory,
    powerDemandByBuildingType: world.powerDemandByBuildingType,
    productionMenuOpen: world.productionMenuOpen,
    itemProductionHistory: world.itemProductionHistory,
    currentTick: world.currentTick,
    handCraftingQueue: world.handCraftingQueue,
    prestigeMenuOpen: world.prestigeMenuOpen,
    prestigeUpgradeState: world.prestigeUpgradeState,
    spaceGalaxyState: world.spaceGalaxyState,
    rendererMode: world.rendererMode,
    quickBeltPreview: world.quickBeltPreview,
    // UI menu states from game engine
    helpMenuOpen: world.helpMenuOpen,
    saveMenuOpen: world.saveMenuOpen,
    leftPanelOpen: world.leftPanelOpen,
    debugMenuOpen: world.debugMenuOpen,
    hasEnabledDebug: world.hasEnabledDebug,
    getTile: world.getTile,
    getTargetPosition: world.getTargetPosition,
    getBuildingTargetLocation: world.getBuildingTargetLocation,
    placeBuilding: world.placeBuilding,
    deconstructBuilding: world.deconstructBuilding,
    upgradeAssemblerToFactory: world.upgradeAssemblerToFactory,
    zoomIn: world.zoomIn,
    zoomOut: world.zoomOut,
    resetZoom: world.resetZoom,
    setTickSpeedMultiplier: world.setTickSpeedMultiplier,
    cycleTickSpeedMultiplier: world.cycleTickSpeedMultiplier,
    saveGame: world.saveGame,
    loadGameFromSaveData: world.loadGameFromSaveData,
    resetGame: world.resetGame,
    collectResource: world.collectResource,
    collectBuildingInventory: world.collectBuildingInventory,
    updateBiome: world.updateBiome,
    movePlayer: world.movePlayer,
    updateTile: world.updateTile,
    addItemToInventory: world.addItemToInventory,
    launchRocket: world.launchRocket,
    forcePrestige: world.forcePrestige,
    startQuickBeltPlacement: world.startQuickBeltPlacement,
    extendQuickBeltPlacement: world.extendQuickBeltPlacement,
    finalizeQuickBeltPreview: world.finalizeQuickBeltPreview,
    cancelQuickBeltPlacement: world.cancelQuickBeltPlacement,
    confirmQuickBeltPlacement: world.confirmQuickBeltPlacement,
    updatePlayerInventory: world.updatePlayerInventory,
    setHasEnabledDebug: world.setHasEnabledDebug,
    setAssemblerInventoryLayer: world.setAssemblerInventoryLayer,
    setChestInventoryLayer: world.setChestInventoryLayer,
    setBeltContentLayer: world.setBeltContentLayer,
    setRejectedItemsLayer: world.setRejectedItemsLayer,
    setMinerTimerLayer: world.setMinerTimerLayer,
    minerOutputBufferLayer: world.minerOutputBufferLayer,
    setMinerOutputBufferLayer: world.setMinerOutputBufferLayer,
    setBuildingLayer: world.setBuildingLayer,
    setSpaceGalaxyState: world.setSpaceGalaxyState,
    preGenerateArea: world.preGenerateArea,
    getBiome: world.getBiome,
    achievements: world.achievements,
    achievementMenuOpen: world.achievementMenuOpen,
    researchMenuOpen: world.researchMenuOpen,
    buildingStatusMenuTarget: world.buildingStatusMenuTarget,
    resourceNodeMenu: world.resourceNodeMenu,
    getAchievements: world.getAchievements,
    triggerAchievementCheck: world.triggerAchievementCheck,
    setBuildingStatusMenuTarget: world.setBuildingStatusMenuTarget,
    openBuildingStatusMenu: world.openBuildingStatusMenu,
    closeBuildingStatusMenu: world.closeBuildingStatusMenu,
    openResourceNodeMenu: world.openResourceNodeMenu,
    closeResourceNodeMenu: world.closeResourceNodeMenu,
    mineResourceNode: world.mineResourceNode,
    researchState: world.researchState,
    unlockResearch: world.unlockResearch,
    forceUnlockResearch: world.forceUnlockResearch,
    isResearchUnlocked: world.isResearchUnlocked,
    getUnlockedResearchIds: world.getUnlockedResearchIds,
    selectResearch: world.selectResearch,
    setResearchMenuOpen: world.setResearchMenuOpen,
    setProductionMenuOpen: world.setProductionMenuOpen,
    setPrestigeMenuOpen: world.setPrestigeMenuOpen,
    selectPrestigeUpgrade: world.selectPrestigeUpgrade,
    purchasePrestigeUpgrade: world.purchasePrestigeUpgrade,
    resetResearch: world.resetResearch,
    resetAllResearch: world.resetAllResearch,
    queueHandCrafting: world.queueHandCrafting,
    setRendererMode: world.setRendererMode,
    toggleRendererMode: world.toggleRendererMode,
  };

  // Use build menu state from the game engine (world state contains it)
  const buildingStateForContext = {
    buildMode: world.selectedBuilding !== null,
    selectedCategory: world.selectedCategory,
    selectedBuilding: world.selectedBuilding,
    buildMenuOpen: world.buildMenuOpen,
    setSelectedCategory: buildingState.setSelectedCategory,
    setSelectedBuilding: buildingState.setSelectedBuilding,
    setBuildMenuOpen: buildingState.setBuildMenuOpen,
    setBuildMenuState: world.setBuildMenuState,
    exitBuildMode: buildingState.exitBuildMode,
  };

  return (
    <TestGlobalManager
      worldState={world}
      buildingState={buildingStateForContext}
    >
      <GameStateManager
        worldState={worldState}
        buildingState={buildingStateForContext}
      >
        <AppContent />
      </GameStateManager>
    </TestGlobalManager>
  );
};

// Main App component with ToastManagerProvider at the top level
const App: React.FC = () => {
  return (
    <ToastManagerProvider>
      <AppWithProviders />
    </ToastManagerProvider>
  );
};

export default App;
